/**
 * Modale per modificare o creare un appuntamento
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert
} from '@mui/material';
import {
  Edit,
  Add,
  Delete,
  Save
} from '@mui/icons-material';
import { BookingEvent } from '../../services/calendarService';
import calendarService from '../../services/calendarService';

interface BookingEditModalProps {
  open: boolean;
  booking: BookingEvent | null;
  onClose: () => void;
  onSave: (booking: any) => void;
  onDelete?: (booking: BookingEvent) => void;
}

const BookingEditModal: React.FC<BookingEditModalProps> = ({
  open,
  booking,
  onClose,
  onSave,
  onDelete
}) => {
  const [formData, setFormData] = useState({
    bookingId: '',
    bookingCode: '',
    patientId: '',
    patientName: '',
    patientFiscalCode: '',
    diaryId: '',
    serviceCode: '',
    serviceName: '',
    nre: '', // Nuovo campo NRE
    start: '',
    end: '',
    priority: 'NORMAL',
    status: 'CONFIRMED',
    notes: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [availableServices, setAvailableServices] = useState<any[]>([]);
  const [loadingServices, setLoadingServices] = useState(false);

  const isNewBooking = !booking?.bookingId;

  useEffect(() => {
    if (booking) {
      const nreValue = booking.extendedProps?.service?.prescription?.nre || booking.extendedProps?.nre || '';

      setFormData({
        bookingId: booking.bookingId || '',
        bookingCode: booking.bookingCode || '',
        patientId: booking.extendedProps?.patient?.id || '',
        patientName: booking.patientName || '',
        patientFiscalCode: booking.patientFiscalCode || '',
        diaryId: booking.diaryId || '',
        serviceCode: booking.serviceCode || '',
        serviceName: booking.serviceName || '',
        nre: nreValue,
        start: booking.start ? new Date(booking.start).toISOString().slice(0, 16) : '',
        end: booking.end ? new Date(booking.end).toISOString().slice(0, 16) : '',
        priority: booking.priority || 'NORMAL',
        status: booking.status || 'CONFIRMED',
        notes: booking.notes || ''
      });
      setErrors({});
    }
  }, [booking]);

  // Carica prestazioni quando cambia l'ID agenda (solo per nuovi appuntamenti)
  useEffect(() => {
    if (isNewBooking && formData.diaryId && open) {
      loadAvailableServices(formData.diaryId);
    }
  }, [formData.diaryId, open, isNewBooking]);

  const loadAvailableServices = async (diaryId: string) => {
    if (!diaryId) return;

    setLoadingServices(true);
    try {
      const diaryData = await calendarService.getDiaryDetails(diaryId);

      // Estrai le prestazioni dall'agenda
      const services = diaryData?.services || diaryData?.service || [];
      const servicesArray = Array.isArray(services) ? services : [services].filter(Boolean);

      setAvailableServices(servicesArray);
    } catch (error) {
      console.error('Error loading services:', error);
      setAvailableServices([]);
    } finally {
      setLoadingServices(false);
    }
  };

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Rimuovi l'errore se il campo viene modificato
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (isNewBooking) {
      if (!formData.patientId) newErrors.patientId = 'Campo obbligatorio';
      if (!formData.serviceCode) newErrors.serviceCode = 'Campo obbligatorio';
      if (!formData.nre) newErrors.nre = 'Campo obbligatorio per prenotazioni SSN';
    }

    if (!formData.diaryId) newErrors.diaryId = 'Campo obbligatorio';
    if (!formData.start) newErrors.start = 'Campo obbligatorio';
    if (!formData.end) newErrors.end = 'Campo obbligatorio';

    // Valida che la data di fine sia dopo quella di inizio
    if (formData.start && formData.end) {
      const startDate = new Date(formData.start);
      const endDate = new Date(formData.end);
      if (endDate <= startDate) {
        newErrors.end = 'La data di fine deve essere dopo quella di inizio';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) return;

    onSave({
      ...formData,
      start: new Date(formData.start),
      end: new Date(formData.end)
    });
  };

  const handleDelete = () => {
    if (booking && onDelete) {
      onDelete(booking);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {isNewBooking ? <Add color="primary" /> : <Edit color="primary" />}
          <Typography variant="h6">
            {isNewBooking ? 'Nuovo Appuntamento' : 'Modifica Appuntamento'}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Info esistente */}
          {!isNewBooking && formData.bookingCode && (
            <Grid item xs={12}>
              <Alert severity="info">
                Codice Prenotazione: <strong>{formData.bookingCode}</strong>
              </Alert>
            </Grid>
          )}

          {/* Dati Paziente */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" color="primary" gutterBottom>
              Dati Paziente
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required={isNewBooking}
              label="ID Paziente"
              value={formData.patientId}
              onChange={(e) => handleChange('patientId', e.target.value)}
              error={!!errors.patientId}
              helperText={errors.patientId || 'ID univoco del paziente'}
              disabled={!isNewBooking}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Nome Paziente"
              value={formData.patientName}
              onChange={(e) => handleChange('patientName', e.target.value)}
              helperText="Nome completo del paziente (info)"
              disabled={!isNewBooking}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Codice Fiscale"
              value={formData.patientFiscalCode}
              onChange={(e) => handleChange('patientFiscalCode', e.target.value)}
              helperText="Codice fiscale paziente (info)"
              disabled={!isNewBooking}
            />
          </Grid>

          {/* Agenda e Prestazione */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" color="primary" gutterBottom>
              Agenda e Prestazione
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="ID Agenda"
              value={formData.diaryId}
              onChange={(e) => handleChange('diaryId', e.target.value)}
              error={!!errors.diaryId}
              helperText={errors.diaryId || 'ID agenda su cui prenotare'}
            />
          </Grid>

          <Grid item xs={12}>
            {isNewBooking && availableServices.length > 0 ? (
              <FormControl fullWidth required error={!!errors.serviceCode}>
                <InputLabel>Prestazione</InputLabel>
                <Select
                  value={formData.serviceCode}
                  label="Prestazione"
                  onChange={(e) => {
                    const selectedService = availableServices.find(s => s.code === e.target.value);
                    handleChange('serviceCode', e.target.value);
                    if (selectedService) {
                      handleChange('serviceName', selectedService.name);
                    }
                  }}
                  disabled={loadingServices}
                >
                  {loadingServices ? (
                    <MenuItem value="">Caricamento prestazioni...</MenuItem>
                  ) : (
                    availableServices.map((service: any) => (
                      <MenuItem key={service.code || service.id} value={service.code}>
                        {service.code} - {service.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
                {errors.serviceCode && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                    {errors.serviceCode}
                  </Typography>
                )}
                {!errors.serviceCode && (
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, ml: 2 }}>
                    Seleziona una prestazione associata all'agenda
                  </Typography>
                )}
              </FormControl>
            ) : isNewBooking ? (
              <TextField
                fullWidth
                required
                label="Codice Prestazione"
                value={formData.serviceCode}
                onChange={(e) => handleChange('serviceCode', e.target.value)}
                error={!!errors.serviceCode}
                helperText={errors.serviceCode || 'Inserisci ID agenda per caricare prestazioni disponibili'}
                disabled={loadingServices}
              />
            ) : (
              <TextField
                fullWidth
                label="Prestazione"
                value={`${formData.serviceCode} - ${formData.serviceName}`}
                disabled
                helperText="Prestazione corrente (non modificabile)"
              />
            )}
          </Grid>

          {/* NRE - Numero Ricetta Elettronica */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required={isNewBooking}
              label="NRE (Numero Ricetta Elettronica)"
              value={formData.nre}
              onChange={(e) => handleChange('nre', e.target.value)}
              error={!!errors.nre}
              helperText={errors.nre || 'Obbligatorio per prenotazioni SSN'}
              disabled={!isNewBooking}
            />
          </Grid>

          {/* Data e Orario */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" color="primary" gutterBottom>
              Data e Orario
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              type="datetime-local"
              label="Data/Ora Inizio"
              value={formData.start}
              onChange={(e) => handleChange('start', e.target.value)}
              error={!!errors.start}
              helperText={errors.start}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              type="datetime-local"
              label="Data/Ora Fine"
              value={formData.end}
              onChange={(e) => handleChange('end', e.target.value)}
              error={!!errors.end}
              helperText={errors.end}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Priorità e Stato */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" color="primary" gutterBottom>
              Priorità e Stato
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Priorità</InputLabel>
              <Select
                value={formData.priority}
                label="Priorità"
                onChange={(e) => handleChange('priority', e.target.value)}
              >
                <MenuItem value="NORMAL">Normale</MenuItem>
                <MenuItem value="URGENT">Urgente</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth disabled={isNewBooking}>
              <InputLabel>Stato</InputLabel>
              <Select
                value={formData.status}
                label="Stato"
                onChange={(e) => handleChange('status', e.target.value)}
              >
                <MenuItem value="CONFIRMED">Confermato</MenuItem>
                <MenuItem value="MODIFIED">Modificato</MenuItem>
                <MenuItem value="CANCELLED">Cancellato</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Note */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Note"
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              helperText="Note aggiuntive per l'appuntamento"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        {!isNewBooking && onDelete && (
          <Button
            onClick={handleDelete}
            color="error"
            startIcon={<Delete />}
            sx={{ mr: 'auto' }}
          >
            Elimina
          </Button>
        )}

        <Button onClick={onClose}>
          Annulla
        </Button>

        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={<Save />}
        >
          Salva
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BookingEditModal;
